import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getDistricts, getNeighborhoods } from '../services/addressService';

// Denetleyici birden fazla ilçedeki mahallelerden sorumlu olabilir (IP-536). Kayıt biçimi
// değişmedi: Users.Ilce virgüllü ilçe ADLARI ("BALÇOVA,KARŞIYAKA"), Users.Mahlist virgüllü
// mahalle adları. Mahlist'i okuyan eski uçlar (SendNotificationCavus, BolgeCavusListesi)
// mahalle adıyla çalıştığı için ilçe öneki eklenmedi.
// Bilinen sınır: aynı adlı mahalle seçili iki ilçede de varsa (YALI: GÜZELBAHÇE/KARŞIYAKA)
// kayıt yalnızca adı tuttuğu için yeniden açılışta iki ilçede de seçili görünür.

const parseApiList = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.Data)) return response.Data;
  return [];
};

const virgulluAyir = (metin) =>
  typeof metin === 'string'
    ? metin.split(',').map((s) => s.trim()).filter((s) => s && s !== 'null' && s !== 'undefined')
    : [];

// DB collation'ı Turkish_CI_AS; "Ilıca"/"ILICA" gibi karışık yazımlar eşleşsin diye
// I/ı/İ/i tek harfe indirilir (bkz. API UserFilterHelper.AdEsit).
const katla = (s) => String(s ?? '').trim().toLocaleLowerCase('tr-TR').replace(/ı/g, 'i');
const adEsit = (a, b) => katla(a) === katla(b);

// Mahalle adı ilçeler arasında tekil değil; seçim anahtarı ilçe id'siyle birlikte tutulur.
const anahtarOlustur = (ilceId, mahalle) => `${ilceId}|${mahalle}`;
const anahtarIlcesi = (anahtar) => anahtar.slice(0, anahtar.indexOf('|'));

const BOS_BULUNAMAYAN = { ilceler: [], mahalleler: [] };

/**
 * @param {boolean} aktif           İlçe/mahalle alanları görünür mü
 * @param {string}  kayitliIlce     Users.Ilce (düzenlemede)
 * @param {string}  kayitliMahlist  Users.Mahlist (düzenlemede)
 */
const useIlceMahalleSecimi = ({ aktif, kayitliIlce = '', kayitliMahlist = '' }) => {
  const [ilceSecenekleri, setIlceSecenekleri] = useState([]);
  const [ilceYukleniyor, setIlceYukleniyor] = useState(false);
  const [ilceYuklendi, setIlceYuklendi] = useState(false);
  // ilçe id -> mahalle adları
  const [mahalleListeleri, setMahalleListeleri] = useState({});
  const istenenIlceler = useRef(new Set());

  const [ilceler, setIlceler] = useState([]);
  const [mahalleler, setMahalleler] = useState([]);
  const [degisti, setDegisti] = useState(false);
  // Kayıtlı değerlerin seçime çevrilmesi: 'bekliyor' -> 'ilce' -> 'tamam'
  const [cozum, setCozum] = useState('bekliyor');
  const [bulunamayanlar, setBulunamayanlar] = useState(BOS_BULUNAMAYAN);

  const sifirla = useCallback(() => {
    setIlceler([]);
    setMahalleler([]);
    setDegisti(false);
    setCozum('bekliyor');
    setBulunamayanlar(BOS_BULUNAMAYAN);
  }, []);

  useEffect(() => {
    if (!aktif) sifirla();
  }, [aktif, sifirla]);

  useEffect(() => {
    if (!aktif || ilceYuklendi) return;
    let iptal = false;
    setIlceYukleniyor(true);
    getDistricts()
      .then((response) => {
        if (iptal) return;
        const liste = parseApiList(response)
          .map((item) => ({
            id: String(item.id ?? item.Id ?? item.ilceId ?? item.code ?? item.value ?? item.ad ?? ''),
            name: item.ad ?? item.name ?? item.IlceAd ?? item.ilceAd ?? item.label ?? item.id ?? ''
          }))
          .filter((item) => item.id && item.name);
        setIlceSecenekleri(liste);
      })
      .catch((err) => {
        console.error('Failed to fetch districts:', err);
      })
      .finally(() => {
        if (iptal) return;
        setIlceYukleniyor(false);
        setIlceYuklendi(true);
      });
    return () => {
      iptal = true;
    };
  }, [aktif, ilceYuklendi]);

  // Seçili her ilçenin mahalleleri bir kez çekilir; hata alan ilçe yeniden seçilince tekrar denenir.
  useEffect(() => {
    if (!aktif) return;
    ilceler.forEach((id) => {
      if (istenenIlceler.current.has(id)) return;
      istenenIlceler.current.add(id);
      getNeighborhoods(id)
        .then((response) => {
          const adlar = [];
          parseApiList(response).forEach((item) => {
            const ad = item.ad ?? item.name ?? item.MahalleAd ?? item.mahalleAd ?? item.label ?? '';
            if (ad && !adlar.some((a) => adEsit(a, ad))) adlar.push(ad);
          });
          return adlar;
        })
        .catch((err) => {
          console.error('Failed to fetch neighborhoods:', err);
          istenenIlceler.current.delete(id);
          return [];
        })
        .then((adlar) => setMahalleListeleri((prev) => ({ ...prev, [id]: adlar })));
    });
  }, [aktif, ilceler]);

  // Kayıtlı ilçeler -> seçenek id'leri. Eski kayıtlarda Users.Ilce'de ad yerine id duruyor.
  useEffect(() => {
    if (!aktif || cozum !== 'bekliyor' || !ilceYuklendi) return;
    const idler = [];
    const yok = [];
    virgulluAyir(kayitliIlce).forEach((kayit) => {
      const ilce = ilceSecenekleri.find((d) => adEsit(d.name, kayit) || d.id === kayit);
      if (!ilce) yok.push(kayit);
      else if (!idler.includes(ilce.id)) idler.push(ilce.id);
    });
    setIlceler(idler);
    setBulunamayanlar({ ilceler: yok, mahalleler: [] });
    setCozum('ilce');
  }, [aktif, cozum, ilceYuklendi, ilceSecenekleri, kayitliIlce]);

  // Kayıtlı mahalleler -> seçili ilçelerin mahalle anahtarları (ilçelerin mahalleleri gelince).
  useEffect(() => {
    if (!aktif || cozum !== 'ilce' || ilceler.some((id) => !(id in mahalleListeleri))) return;
    const anahtarlar = [];
    const yok = [];
    virgulluAyir(kayitliMahlist).forEach((kayit) => {
      let bulundu = false;
      ilceler.forEach((id) => {
        const ad = mahalleListeleri[id].find((m) => adEsit(m, kayit));
        if (ad) {
          bulundu = true;
          anahtarlar.push(anahtarOlustur(id, ad));
        }
      });
      if (!bulundu) yok.push(kayit);
    });
    setMahalleler((prev) => [...new Set([...anahtarlar, ...prev])]);
    setBulunamayanlar((prev) => ({ ...prev, mahalleler: yok }));
    setCozum('tamam');
  }, [aktif, cozum, ilceler, mahalleListeleri, kayitliMahlist]);

  const ilceAdi = useCallback(
    (id) => ilceSecenekleri.find((d) => d.id === id)?.name ?? id,
    [ilceSecenekleri]
  );

  // MultiSelect grup seçenekleri: her seçili ilçe bir grup. Birden çok ilçe seçiliyken çipte
  // ilçe adı da görünür, aksi halde aynı adlı mahalleler ayırt edilemez.
  const mahalleGruplari = useMemo(
    () =>
      ilceler
        .filter((id) => mahalleListeleri[id]?.length)
        .map((id) => {
          const ilce = ilceAdi(id);
          return {
            ilceAdi: ilce,
            items: mahalleListeleri[id].map((ad) => ({
              anahtar: anahtarOlustur(id, ad),
              ad,
              etiket: ilceler.length > 1 ? `${ad} (${ilce})` : ad
            }))
          };
        }),
    [ilceler, mahalleListeleri, ilceAdi]
  );

  const mahalleYukleniyor = aktif && ilceler.some((id) => !(id in mahalleListeleri));

  // Dokunulmamış ve tam çözülemeyen kayıt (ilçe listesi gelmedi, eski id, listede olmayan ad)
  // olduğu gibi geri yazılır; aksi halde denetleyicinin ilçe/mahallesi sessizce silinirdi.
  const korunuyor =
    !degisti &&
    (cozum !== 'tamam' || bulunamayanlar.ilceler.length > 0 || bulunamayanlar.mahalleler.length > 0);

  const ilceDegistir = (idler) => {
    const yeni = idler || [];
    setIlceler(yeni);
    setMahalleler((prev) => prev.filter((a) => yeni.includes(anahtarIlcesi(a))));
    setDegisti(true);
  };

  const mahalleDegistir = (anahtarlar) => {
    setMahalleler(anahtarlar || []);
    setDegisti(true);
  };

  // Users.Ilce / Users.Mahlist'e yazılacak değerler.
  const kayitDegerleri = () => {
    if (korunuyor) return { ilce: kayitliIlce || '', mahlist: kayitliMahlist || '' };
    const mahalleAdlari = [];
    ilceler.forEach((id) =>
      (mahalleListeleri[id] || []).forEach((ad) => {
        if (mahalleler.includes(anahtarOlustur(id, ad)) && !mahalleAdlari.some((m) => adEsit(m, ad))) {
          mahalleAdlari.push(ad);
        }
      })
    );
    return { ilce: ilceler.map(ilceAdi).join(','), mahlist: mahalleAdlari.join(',') };
  };

  // Seçili mahalleler (ilçe ADI, mahalle adı) çifti olarak; Bildirimler ekranı API'ye "İLÇE|MAHALLE" gönderir.
  const secilenCiftler = () =>
    mahalleler.map((a) => ({ ilce: ilceAdi(anahtarIlcesi(a)), mahalle: a.slice(a.indexOf('|') + 1) }));

  // Denetleyici için hata mesajı; geçerliyse null.
  const dogrula = () => {
    if (korunuyor) {
      if (!virgulluAyir(kayitliIlce).length) return 'Denetleyici için ilçe seçiniz.';
      if (!virgulluAyir(kayitliMahlist).length) return 'Denetleyici için en az bir mahalle seçiniz.';
      return null;
    }
    if (!ilceler.length) return 'Denetleyici için ilçe seçiniz.';
    if (mahalleYukleniyor) return 'Mahalleler yükleniyor, lütfen bekleyiniz.';
    const eksik = ilceler.find((id) => !mahalleler.some((a) => anahtarIlcesi(a) === id));
    if (eksik) return `${ilceAdi(eksik)} ilçesi için en az bir mahalle seçiniz.`;
    return null;
  };

  return {
    ilceSecenekleri,
    ilceYukleniyor,
    ilceler,
    ilceDegistir,
    mahalleGruplari,
    mahalleYukleniyor,
    mahalleler,
    mahalleDegistir,
    degisti,
    bulunamayanlar,
    kayitDegerleri,
    secilenCiftler,
    dogrula,
    sifirla
  };
};

export default useIlceMahalleSecimi;
